import React from "react";

const OperationsPanel = ({ state, actions, helpers, constants, bracket }) => {
  const {
    isBracketFormat,
    bracketMatchupTotal,
    bracketLoading,
    rounds,
    bracketRoundsForTree,
    selectedRoundId,
    selectedLobbyId,
    entryTypeLabel,
    entryTypeLabelSingle,
    lobbyLabel,
    lobbyLabelPlural,
    lobbies,
    selectedRound,
    selectedLobby,
    activeBestOf,
    activeBestOfWins,
    roundForm,
    bestOfValue,
    format,
    lobbyForm,
    saving,
  } = state;
  const {
    setSelectedRoundId,
    setSelectedLobbyId,
    setActivePanel,
    handleDeleteLobby,
    createRound,
    setRoundForm,
    createLobby,
    setLobbyForm,
    handleDeleteRound,
    openScheduleModal,
  } = actions;
  const {
    getNumber,
    formatBestOfLabel,
    getBestOfWins,
    getMatchupSlots,
    getMatchupSummary,
    getRoundLabel,
    getRoundLayoutStyles,
    getSeedPlaceholder,
    formatDateTime,
  } = helpers;
  const { BEST_OF_OPTIONS, FORMAT_LABELS, QUALIFICATION_LABELS } = constants;
  const {
    bracketTreeRef,
    bracketCardRefs,
    bracketViewBoxWidth,
    bracketViewBoxHeight,
    bracketConnectors,
  } = bracket;

  return (
    <div className="manage-grid">
      {isBracketFormat && (
        <div className="card manage-full bracket-tree-card">
          <div className="card-header">
            <h3>Bracket tree</h3>
            <span className="chip">{bracketMatchupTotal} matchups</span>
          </div>
          {bracketLoading ? (
            <div className="empty-inline">Loading bracket tree...</div>
          ) : rounds.length === 0 ? (
            <div className="empty-inline">Create a round to start the bracket.</div>
          ) : (
            <div className="bracket-tree" ref={bracketTreeRef}>
              <svg
                className="bracket-connectors"
                width={bracketViewBoxWidth}
                height={bracketViewBoxHeight}
                viewBox={`0 0 ${bracketViewBoxWidth} ${bracketViewBoxHeight}`}
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                {bracketConnectors.map((connector) => (
                  <path key={connector.id} d={connector.d} />
                ))}
              </svg>
              {bracketRoundsForTree.map((round, index) => {
                const roundBestOf = getNumber(round.bestOf) || 1;
                const roundMatchups = round.lobbies?.length || 0;
                const matchupLabel =
                  roundMatchups === 1 ? "matchup" : "matchups";
                const roundLabel = getRoundLabel(round, index);
                return (
                  <div
                    key={round.id}
                    className={`bracket-round ${
                      round.id === selectedRoundId ? "active" : ""
                    }`}
                    style={getRoundLayoutStyles(index)}
                  >
                    <div className="bracket-round-label">
                      <div>
                        <div className="bracket-round-name">{roundLabel}</div>
                        <div className="bracket-round-meta">
                          {formatBestOfLabel(roundBestOf)} · {roundMatchups} {" "}
                          {matchupLabel}
                        </div>
                      </div>
                      {round.id === selectedRoundId ? (
                        <span className="status-pill accent">Active</span>
                      ) : null}
                    </div>
                    {roundMatchups === 0 ? (
                      <div className="empty-inline">No matchups yet.</div>
                    ) : (
                      <div className="bracket-grid">
                        {round.lobbies.map((lobby, lobbyIndex) => {
                          const entries = Array.isArray(lobby.entries)
                            ? lobby.entries
                            : [];
                          const slots = getMatchupSlots(entries);
                          const entryCount = entries.filter(Boolean).length;
                          const overflow = Math.max(entryCount - 2, 0);
                          const entryCountLabel =
                            entryCount === 1
                              ? entryTypeLabelSingle
                              : entryTypeLabel;
                          const summary = getMatchupSummary(lobby, roundBestOf);
                          const showScores =
                            summary.scoreA !== null && summary.scoreB !== null;
                          const lobbyNumber =
                            getNumber(lobby.lobbyNumber) || lobbyIndex + 1;
                          return (
                            <div
                              key={lobby.id}
                              className={`bracket-card ${
                                lobby.id === selectedLobbyId ? "active" : ""
                              }`}
                              ref={(node) => {
                                const key = `${round.id}:${lobby.id}`;
                                if (node) {
                                  bracketCardRefs.current.set(key, node);
                                } else {
                                  bracketCardRefs.current.delete(key);
                                }
                              }}
                              onClick={() => {
                                setSelectedRoundId(round.id);
                                setSelectedLobbyId(lobby.id);
                                setActivePanel("lobby");
                              }}
                              role="button"
                              tabIndex={0}
                            >
                              <div className="bracket-entries">
                                {slots.map((entry, slotIndex) => {
                                  const placeholder = entry
                                    ? { name: "", meta: "" }
                                    : getSeedPlaceholder(
                                        bracketRoundsForTree,
                                        index,
                                        lobbyNumber,
                                        slotIndex
                                      );
                                  const entryName = entry
                                    ? entry.name ||
                                      entry.inGameName ||
                                      entry.username ||
                                      entryTypeLabelSingle
                                    : placeholder.name;
                                  const entryMeta = entry
                                    ? entry.type === "team"
                                      ? `${
                                          entry.members?.length ||
                                          entry.memberIds?.length ||
                                          0
                                        } players`
                                      : entry.inGameName &&
                                        entry.inGameName !== entry.name
                                      ? entry.inGameName
                                      : entry.inGameId || ""
                                    : placeholder.meta;
                                  const entryScore = showScores
                                    ? slotIndex === 0
                                      ? summary.scoreA
                                      : summary.scoreB
                                    : null;
                                  const isWinner =
                                    entry &&
                                    summary.winnerId &&
                                    entry.id === summary.winnerId;
                                  const isPlaceholder = !entry;
                                  const showBadgeOrScore =
                                    (showScores && entry) || isWinner;
                                  return (
                                    <div
                                      key={`${lobby.id}-slot-${slotIndex}`}
                                      className={`bracket-entry ${
                                        isPlaceholder ? "placeholder" : ""
                                      } ${isWinner ? "winner" : ""}`}
                                    >
                                      <span className="bracket-seed">
                                        Slot {slotIndex + 1}
                                      </span>
                                      <div className="bracket-entry-body">
                                        <div className="bracket-name">
                                          {entryName}
                                        </div>
                                        {entryMeta ? (
                                          <div className="bracket-entry-meta">
                                            {entryMeta}
                                          </div>
                                        ) : null}
                                      </div>
                                      {showBadgeOrScore ? (
                                        <div className="bracket-entry-score">
                                          {showScores && entry ? (
                                            <span className="bracket-score">
                                              {entryScore}
                                            </span>
                                          ) : null}
                                          {isWinner ? (
                                            <span className="status-pill success">
                                              Winner
                                            </span>
                                          ) : null}
                                        </div>
                                      ) : null}
                                    </div>
                                  );
                                })}
                                {overflow > 0 ? (
                                  <div className="bracket-overflow">
                                    +{overflow} more
                                  </div>
                                ) : null}
                              </div>
                              <div
                                className="bracket-connector"
                                aria-hidden="true"
                              />
                              <div className="bracket-meta">
                                <div className="bracket-title">
                                  {lobby.lobbyName}
                                </div>
                                <div className="bracket-subtitle">
                                  {entryCount} {entryCountLabel}
                                </div>
                                <span
                                  className={`status-pill ${
                                    lobby.roomId ? "success" : "warning"
                                  }`}
                                >
                                  {lobby.roomId ? "Credentials ready" : "Pending"}
                                </span>
                              </div>
                              <div className="bracket-actions">
                                <button
                                  className="button danger small"
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleDeleteLobby(lobby.id, round.id);
                                  }}
                                  disabled={saving}
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      <div className="manage-column">
        <div className="card form-card">
          <div className="card-header">
            <h3>Create round</h3>
          </div>
          <label className="field">
            <span>Round name</span>
            <input
              value={roundForm.name}
              onChange={(event) =>
                setRoundForm((prev) => ({
                  ...prev,
                  name: event.target.value,
                }))
              }
              placeholder="Round 1"
            />
          </label>
          <label className="field">
            <span>Round number</span>
            <input
              type="number"
              value={roundForm.number}
              onChange={(event) =>
                setRoundForm((prev) => ({
                  ...prev,
                  number: event.target.value,
                }))
              }
            />
          </label>
          <label className="field">
            <span>Scheduled start</span>
            <input
              type="datetime-local"
              value={roundForm.scheduledStart}
              onChange={(event) =>
                setRoundForm((prev) => ({
                  ...prev,
                  scheduledStart: event.target.value,
                }))
              }
            />
          </label>
          <label className="field">
            <span>Scheduled end</span>
            <input
              type="datetime-local"
              value={roundForm.scheduledEnd}
              onChange={(event) =>
                setRoundForm((prev) => ({
                  ...prev,
                  scheduledEnd: event.target.value,
                }))
              }
            />
          </label>
          {isBracketFormat && (
            <div className="form-section">
              <div className="section-title">Series format</div>
              <div className="manage-tabs">
                {BEST_OF_OPTIONS.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={`manage-tab ${
                      bestOfValue === option ? "active" : ""
                    }`}
                    onClick={() =>
                      setRoundForm((prev) => ({
                        ...prev,
                        bestOf: option,
                      }))
                    }
                  >
                    {formatBestOfLabel(option)}
                  </button>
                ))}
              </div>
              <div className="helper-text">
                First to {getBestOfWins(bestOfValue)} wins.
              </div>
            </div>
          )}
          {format === "battle_royale" && (
            <label className="field">
              <span>Teams per lobby</span>
              <input
                type="number"
                value={roundForm.teamsPerLobby}
                onChange={(event) =>
                  setRoundForm((prev) => ({
                    ...prev,
                    teamsPerLobby: event.target.value,
                  }))
                }
              />
            </label>
          )}
          {format === "battle_royale" && (
            <>
              <label className="field">
                <span>Qualification method</span>
                <select
                  value={roundForm.qualificationMethod}
                  onChange={(event) =>
                    setRoundForm((prev) => ({
                      ...prev,
                      qualificationMethod: event.target.value,
                    }))
                  }
                >
                  <option value="placement">Placement</option>
                  <option value="totalPoints">Total points</option>
                </select>
              </label>
              <label className="field">
                <span>Teams to qualify</span>
                <input
                  type="number"
                  value={roundForm.qualifyCount}
                  onChange={(event) =>
                    setRoundForm((prev) => ({
                      ...prev,
                      qualifyCount: event.target.value,
                    }))
                  }
                />
              </label>
            </>
          )}
          <label className="field">
            <span>
              {isBracketFormat
                ? "Auto-create bracket rounds"
                : "Auto-create lobbies"}
            </span>
            <select
              value={roundForm.autoCreate ? "yes" : "no"}
              onChange={(event) =>
                setRoundForm((prev) => ({
                  ...prev,
                  autoCreate: event.target.value === "yes",
                }))
              }
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </label>
          <button className="button primary" type="button" onClick={createRound}>
            {saving ? "Saving..." : "Create round"}
          </button>
        </div>

        <div className="card">
          <div className="card-header">
            <h3>Rounds</h3>
            <span className="chip">{rounds.length} total</span>
          </div>
          {rounds.length === 0 ? (
            <div className="empty-inline">No rounds created yet.</div>
          ) : (
            <div className="list">
              {rounds.map((round) => {
                const roundFormat = round.format || format;
                const roundBestOf =
                  roundFormat !== "battle_royale"
                    ? getNumber(round.bestOf) || 1
                    : null;
                const meta = [
                  roundBestOf ? formatBestOfLabel(roundBestOf) : null,
                  round.teamsPerLobby ? `${round.teamsPerLobby} per lobby` : null,
                  round.qualifyCount ? `Qualify ${round.qualifyCount}` : null,
                ]
                  .filter(Boolean)
                  .join(" · ");
                return (
                  <div
                    key={round.id}
                    className={`list-row ${
                      round.id === selectedRoundId ? "active-row" : ""
                    }`}
                    onClick={() => setSelectedRoundId(round.id)}
                    role="button"
                    tabIndex={0}
                  >
                    <div>
                      <div className="list-title">{round.roundName}</div>
                      <div className="list-subtitle">
                        {FORMAT_LABELS[round.format] || "Format"} · Round {" "}
                        {round.roundNumber}
                        {round.scheduledStart
                          ? ` · ${formatDateTime(round.scheduledStart)}`
                          : ""}
                      </div>
                    </div>
                    <div className="list-actions">
                      <div className="list-meta">{meta}</div>
                      <button
                        className="button danger small"
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleDeleteRound(round.id);
                        }}
                        disabled={saving}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="card form-card">
          <div className="card-header">
            <h3>Create {lobbyLabel}</h3>
          </div>
          {!selectedRoundId ? (
            <div className="empty-inline">
              Select a round to create {lobbyLabelPlural.toLowerCase()}.
            </div>
          ) : (
            <>
              <label className="field">
                <span>{lobbyLabel} name</span>
                <input
                  value={lobbyForm.name}
                  onChange={(event) =>
                    setLobbyForm((prev) => ({
                      ...prev,
                      name: event.target.value,
                    }))
                  }
                  placeholder={format === "battle_royale" ? "Lobby 1" : "Matchup 1"}
                />
              </label>
              <label className="field">
                <span>{lobbyLabel} number</span>
                <input
                  type="number"
                  value={lobbyForm.number}
                  onChange={(event) =>
                    setLobbyForm((prev) => ({
                      ...prev,
                      number: event.target.value,
                    }))
                  }
                />
              </label>
              <button className="button primary" type="button" onClick={createLobby}>
                Add {lobbyLabel.toLowerCase()}
              </button>
            </>
          )}
        </div>

        {!isBracketFormat && (
          <div className="card">
            <div className="card-header">
              <h3>{lobbyLabelPlural}</h3>
              <span className="chip">{lobbies.length} total</span>
            </div>
            {!selectedRoundId ? (
              <div className="empty-inline">
                Select a round to manage {lobbyLabelPlural.toLowerCase()}.
              </div>
            ) : lobbies.length === 0 ? (
              <div className="empty-inline">
                No {lobbyLabelPlural.toLowerCase()} yet.
              </div>
            ) : (
              <div className="list">
                {lobbies.map((lobby) => (
                  <div
                    key={lobby.id}
                    className={`list-row ${
                      lobby.id === selectedLobbyId ? "active-row" : ""
                    }`}
                    onClick={() => {
                      setSelectedLobbyId(lobby.id);
                      setActivePanel("lobby");
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <div>
                      <div className="list-title">{lobby.lobbyName}</div>
                      <div className="list-subtitle">
                        {lobby.entries?.length || 0} {entryTypeLabel}
                      </div>
                    </div>
                    <div className="list-actions">
                      <div className="list-meta">
                        {lobby.roomId ? "Credentials ready" : "Pending"}
                      </div>
                      <button
                        className="button danger small"
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleDeleteLobby(lobby.id);
                        }}
                        disabled={saving}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="manage-column">
        <div className="card">
          <div className="card-header">
            <h3>Round summary</h3>
          </div>
          {!selectedRound ? (
            <div className="empty-inline">Select a round to review.</div>
          ) : (
            <>
              <div className="detail-body">
                <div className="detail-row">
                  <div className="detail-label">Round</div>
                  <div className="detail-value">{selectedRound.roundName}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">Schedule</div>
                  <div className="detail-value">
                    {formatDateTime(selectedRound.scheduledStart)} → {" "}
                    {formatDateTime(selectedRound.scheduledEnd)}
                  </div>
                </div>
                {isBracketFormat && activeBestOf ? (
                  <div className="detail-row">
                    <div className="detail-label">Series</div>
                    <div className="detail-value">
                      Best of {activeBestOf} · first to {activeBestOfWins} wins
                    </div>
                  </div>
                ) : null}
                <div className="detail-row">
                  <div className="detail-label">Qualification</div>
                  <div className="detail-value">
                    {QUALIFICATION_LABELS[selectedRound.qualificationMethod] ||
                      "Winner"}
                    {selectedRound.qualifyCount
                      ? ` · Top ${selectedRound.qualifyCount}`
                      : ""}
                  </div>
                </div>
              </div>
              <div className="inline-actions">
                <button
                  className="button primary"
                  type="button"
                  onClick={openScheduleModal}
                  disabled={saving}
                >
                  {saving ? "Working..." : "Create next round"}
                </button>
              </div>
            </>
          )}
        </div>

        <div className="card">
          <div className="card-header">
            <h3>{lobbyLabel} workspace</h3>
          </div>
          {!selectedLobby ? (
            <div className="empty-inline">
              Select a {lobbyLabel.toLowerCase()} to open workspace.
            </div>
          ) : (
            <>
              <div className="detail-body">
                <div className="detail-row">
                  <div className="detail-label">{lobbyLabel}</div>
                  <div className="detail-value">{selectedLobby.lobbyName}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">Entries</div>
                  <div className="detail-value">
                    {selectedLobby.entries?.length || 0} {entryTypeLabel}
                  </div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">Credentials</div>
                  <div className="detail-value">
                    {selectedLobby.roomId ? "Ready" : "Pending"}
                  </div>
                </div>
              </div>
              <button
                className="button ghost"
                type="button"
                onClick={() => setActivePanel("lobby")}
              >
                Open {lobbyLabel.toLowerCase()} workspace
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OperationsPanel;
